import { AlertCircle, CheckCircle2, WifiOff, X } from 'lucide-solid';
import { For, Show, splitProps, type JSX } from 'solid-js';
import { effectUnavailableMessage } from '../lib/operatorCopy';
import { can } from '../lib/permissions.generated';
import { usePressFeedback } from '../state/useBusyFeedback';
import { BusySlot, BusySpinner, Spinner } from './busy';
import { useVenue } from '../state/VenueProvider';
import { TEST_IDS } from '../testIds';

/**
 * The busy primitives live in `busy.tsx` — dependency-free, so that a control tested in the server
 * environment can import them — and are re-exported here so a screen still takes its controls from
 * one place.
 */
export { BusySlot, BusySpinner, Spinner };

export function ScreenHeader(props: {
  title: string;
  description: string;
  actions?: JSX.Element;
  kicker?: string;
}): JSX.Element {
  return (
    <header class="screen-header">
      <div>
        <Show when={props.kicker}><p class="screen-kicker">{props.kicker}</p></Show>
        <h1>{props.title}</h1>
        <p>{props.description}</p>
      </div>
      <Show when={props.actions}><div class="screen-actions">{props.actions}</div></Show>
    </header>
  );
}

export function EmptyState(props: {
  icon?: JSX.Element;
  title: string;
  detail: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <div class="empty-state" role="status">
      <div class="empty-icon">{props.icon ?? <WifiOff size={24} />}</div>
      <h2>{props.title}</h2>
      <p>{props.detail}</p>
      {props.action}
    </div>
  );
}

export function InlineNotice(props: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: string;
  children?: JSX.Element;
  onDismiss?: () => void;
}): JSX.Element {
  return (
    <div class={`notice notice-${props.tone ?? 'info'}`} role={props.tone === 'danger' ? 'alert' : 'status'}>
      <span class="notice-icon">
        {props.tone === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
      </span>
      <div>
        <Show when={props.title}><strong>{props.title}</strong></Show>
        <Show when={props.children}><div>{props.children}</div></Show>
      </div>
      <Show when={props.onDismiss}>
        <button type="button" class="icon-button notice-dismiss" aria-label="Dismiss" onClick={() => props.onDismiss?.()}>
          <X size={16} />
        </button>
      </Show>
    </div>
  );
}

/**
 * Every button that dispatches a command, in two beats.
 *
 * Beat one is the press itself: `is-pressed` lands in the same frame as the click, before anything is
 * sent, so no control ever looks untouched after a tap. Beat two is the spinner, bound to this
 * action's key — `runAction` claims the key synchronously, so it appears in that same frame too, and
 * it clears when the controller acks or refuses. A press that opens a confirmation instead of sending
 * anything lets go of beat one on the next turn of the loop: the dialog is the feedback then.
 *
 * Both beats are bound to THIS action's key, and so is the disable. The point of the interlock is that a
 * command cannot be double-fired, which is a statement about one action, not about the console: it used
 * to disable on `actionBusy()` being set at all, so any command in flight anywhere deadened every button
 * on every screen. That was invisible while acknowledgements came back in half a second. On prod,
 * 2026-09-17, the relay was taking 9–18 s to answer a command the room had already performed, and
 * pressing Blackout — or anything else — left the operator with an entire console of dead controls for
 * up to eighteen seconds. Keying the disable on the action preserves the interlock and drops the
 * collateral.
 *
 * Beat two is the same in every button in the console: the spinner REPLACES the label. Owner ruling,
 * 2026-09-17 — *"The spinner just... The spinner replaces the text on the button when we press it."*
 * The label is hidden where it stands, so the button keeps the exact width it had, and the spinner is
 * drawn over it. There used to be a second presentation, chosen per call site: a 15px slot in front of
 * the label, holding a hidden spinner. It reserved that room when idle too, which pushed every label
 * 11.5px right of its button's centre and made every button 23px wider than its text — the "weird empty
 * gap" reported on `Take over` and `Save preset`, and present on eleven other controls. There is one
 * presentation now, and no prop to get it wrong with.
 */
export interface BusyButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The `runAction` key this button dispatches under.
   *
   * Empty means the button dispatches nothing the console tracks — the only one is a confirmation's
   * accept that releases a command already in flight under its own key, and already spinning on the
   * control that started it. An idle console reports an empty `actionBusy()`, so an empty key must
   * never match it.
   */
  busyKey: string;
}

export function BusyButton(props: BusyButtonProps): JSX.Element {
  const venue = useVenue();
  const busy = () => props.busyKey !== '' && venue.actionBusy() === props.busyKey;
  const press = usePressFeedback(busy);
  const [own, rest] = splitProps(props, [
    'busyKey', 'class', 'classList', 'onClick', 'children', 'disabled', 'type',
  ]);
  return (
    <button
      {...rest}
      type={own.type ?? 'button'}
      class={own.class ?? 'button button-primary'}
      classList={{
        ...own.classList,
        'busy-host': true,
        'is-pressed': press.pressed(),
        'is-busy': busy(),
      }}
      disabled={own.disabled || busy()}
      onClick={(event) => {
        press.press();
        callHandler(own.onClick, event);
      }}
      data-busy={busy() ? 'true' : 'false'}
    >
      <span class="busy-content">{own.children}</span>
      <Show when={busy()}><BusySpinner /></Show>
    </button>
  );
}

/** Solid hands an event handler back as either a function or a `[handler, data]` pair. */
function callHandler(
  handler: JSX.EventHandlerUnion<HTMLButtonElement, MouseEvent> | undefined,
  event: MouseEvent & { currentTarget: HTMLButtonElement; target: Element },
): void {
  if (typeof handler === 'function') handler(event);
  else if (Array.isArray(handler)) handler[0](handler[1], event);
}

/**
 * The box every repeating list of cards or rows sits in.
 *
 * One kind of section, everywhere: a bordered surface that holds the list's own heading and search
 * inside its border, and a body of fixed height that scrolls within itself. A room that fills up with
 * scenes, scores, controllers or effects therefore never pushes what is BELOW the section down the
 * page — the panel under a card grid stays exactly where the operator left it. Anything that must not
 * scroll away with the cards (a search field, a "nothing matches" line) goes in `tools` or `footer`.
 */
export function CardSection(props: {
  class?: string;
  bodyClass?: string;
  heading?: JSX.Element;
  tools?: JSX.Element;
  footer?: JSX.Element;
  /** Names the scroll region for anyone not looking at the screen, where no heading does. */
  label?: string;
  testId?: string;
  bodyTestId?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <section class={`surface card-section ${props.class ?? ''}`.trim()} data-testid={props.testId}>
      {props.heading}
      {props.tools}
      <div
        class={`card-section-body inner-scroll ${props.bodyClass ?? ''}`.trim()}
        role={props.label ? 'group' : undefined}
        aria-label={props.label}
        data-testid={props.bodyTestId}
      >
        {props.children}
      </div>
      {props.footer}
    </section>
  );
}

export function ControlGate(props: { children: JSX.Element }): JSX.Element {
  const venue = useVenue();
  return (
    <Show when={venue.canControl()} fallback={
      <Show when={!venue.rigUnassigned()}><InlineNotice tone="warning" title="View only" /></Show>
    }>
      {props.children}
    </Show>
  );
}

export function Metric(props: { label: string; value: JSX.Element; hint?: JSX.Element; tone?: string }): JSX.Element {
  return (
    <div class={`metric ${props.tone ? `metric-${props.tone}` : ''}`}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
      <Show when={props.hint}><small>{props.hint}</small></Show>
    </div>
  );
}

/**
 * The console's dialog. Anything that must stay put while the body scrolls — the actions a dialog is
 * asking for — goes in `footer`, which sits in a band of its own below the scrolling body, mirroring
 * the title band above it.
 */
export function Modal(props: {
  title: string;
  open: boolean;
  onClose: () => void;
  footer?: JSX.Element;
  testId?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <Show when={props.open}>
      <div class="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
        <section class="modal" role="dialog" aria-modal="true" aria-label={props.title} data-testid={props.testId}>
          <header>
            <h2>{props.title}</h2>
            <button class="icon-button" aria-label="Close" onClick={props.onClose}><X size={20} /></button>
          </header>
          <div class="modal-body">{props.children}</div>
          {props.footer}
        </section>
      </div>
    </Show>
  );
}

/**
 * A yes/no question in the console's own chrome. The repo forbids native `confirm`/`alert` — they look
 * like the browser rather than the product, they cannot be styled or tested, and on a venue iPad they
 * are easy to miss.
 *
 * Every confirmation in the console is this component, and its answer sits in the dialog's footer band
 * rather than loose under the question: a two-word confirmation ("Sunday slow") and a full-sentence one
 * are then the same dialog, with the same shape, instead of one of them reading as a fragment with two
 * buttons after it.
 */
export function ConfirmModal(props: {
  open: boolean;
  title: string;
  detail?: string;
  confirmLabel: string;
  /**
   * The `runAction` key the accept dispatches under, when it dispatches one of its own. The takeover
   * dialog does not: answering it releases a command that is already in flight, under its own key.
   */
  busyKey?: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <Modal
      title={props.title}
      open={props.open}
      onClose={props.onCancel}
      testId={TEST_IDS.confirmModal}
      footer={(
        <div class="modal-actions">
          <button class="button button-secondary" onClick={props.onCancel} data-testid={TEST_IDS.confirmCancel}>Cancel</button>
          {/* The accept is a `BusyButton` like every other command button, rather than hand-rolling the
              busy state — which is how it came to be the one button in the console still reserving a
              slot after the rest had stopped. */}
          <BusyButton
            busyKey={props.busyKey ?? ''}
            class={`button button-${props.tone ?? 'primary'}`}
            onClick={props.onConfirm}
            data-testid={TEST_IDS.confirmAccept}
          >{props.confirmLabel}</BusyButton>
        </div>
      )}
    >
      <Show when={props.detail}><p class="confirm-detail">{props.detail}</p></Show>
    </Modal>
  );
}

/**
 * The raw reason, folded away, for the people who can act on it.
 *
 * The copy law (`lib/operatorCopy.ts`) keeps our nouns off an operator's screen — but the reason still
 * has to be READABLE by whoever can fix it, or the console has simply hidden the diagnosis from the
 * person holding the laptop. So every "this cannot be done" sentence carries its detail here, collapsed,
 * behind one word, visible to whoever may change the room's setup.
 *
 * The audience is exactly the `rig.manage` tier — owner, manager, and Nuance staff. It is read off the
 * permission table rather than a role array so it cannot drift from what the cloud enforces, and so a
 * read-only support session and the cable console (whose paired credential claims only `operator`) both
 * correctly see nothing.
 */
export function TechnicalDetails(props: { detail?: string; testId?: string }): JSX.Element {
  const venue = useVenue();
  return (
    <Show when={props.detail && can(venue.role(), 'rig.manage')}>
      <details class="technical-details" data-testid={props.testId}>
        <summary>Details</summary>
        <p>{props.detail}</p>
      </details>
    </Show>
  );
}

/**
 * One effect cannot run in this room, said once, the same way, everywhere.
 *
 * Every effect panel reached its own wording for this before — six sentences naming published maps, rig
 * versions and what to rebuild, none of which an operator can act on. There is one sentence now, and the
 * six different reasons behind it travel as `detail`.
 *
 * The generic manifest-driven effect panel that replaces the bespoke ones inherits this component as-is.
 */
export function EffectUnavailable(props: {
  effectName: string;
  detail?: string;
  testId: string;
  /**
   * The controller's own sentence, when it wrote one an operator may read ("No music input — pick one on
   * the Music tab"). It names the thing to go and do, which the default sentence cannot, so it wins. The
   * caller is responsible for having checked it against the copy law — `operatorUnavailableReason` in
   * `lib/effectPackages.ts` is that check — because the same controller field also carries raw failures.
   */
  reason?: string;
}): JSX.Element {
  return (
    <div class="engine-blocked" data-testid={props.testId}>
      <p>{props.reason || effectUnavailableMessage(props.effectName)}</p>
      <TechnicalDetails detail={props.detail} />
    </div>
  );
}

export function ToastStack(): JSX.Element {
  const venue = useVenue();
  return (
    <div class="toast-stack" aria-live="polite" aria-atomic="false">
      <For each={venue.toasts()}>
        {(toast) => (
          <div class={`toast toast-${toast.tone}`}>
            <span>{toast.tone === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}</span>
            <p>{toast.message}</p>
            <button aria-label="Dismiss" onClick={() => venue.dismissToast(toast.id)}><X size={16} /></button>
          </div>
        )}
      </For>
    </div>
  );
}
